<script setup lang="ts">
import { ref, computed, onBeforeUnmount, getCurrentInstance } from 'vue';
import LoadingStatus from '@/assets/js/enum/LoadingStatus';
import { dragElement } from '@/assets/js/utils';
import BreadCrumb from '@/components/general/BreadCrumb.vue';
import MainContentPanel from '@/components/display/general/MainContentPanel.vue';
import UserDocuments from '@/components/user/UserDocuments.vue';
import BasicModal from '@/components/display/general/BasicModal.vue';
import NewBasisForm from '@/components/form/NewBasisForm.vue';
import { useState, useActions } from '@/composables/useVuexHelpers';
import UIkit from 'uikit';

interface CustomField {
  name: string;
  value: string;
}

interface DocumentData {
  discussion_type: string;
}

interface CreateDocumentResult {
  team: { team: string };
  document: { document: string };
}

const instance = getCurrentInstance();

const { userData, teamLeadAccess } = useState(['userData', 'teamLeadAccess']);

const { loadUserDocuments, createNewDocument, updateTeam } = useActions([
  'loadUserDocuments',
  'createNewDocument',
  'updateTeam',
]);

const submitDiscussionStatus = ref(LoadingStatus.NONE);
const title = ref('MyDiscussions');
const discussionFormModal = ref<HTMLElement>();

const addNewDisable = computed(() => {
  return !teamLeadAccess.value.allowAddNewDebates ||
    (!teamLeadAccess.value.allowMakeCritique && !teamLeadAccess.value.allowInviteCritique)
    ? true
    : false;
});

const openBeginDiscussion = () => {
  if (discussionFormModal.value) {
    UIkit.modal(discussionFormModal.value).show();
    dragElement(document.getElementById('moveableBasisForm'), 'basisFormModalId');
  }
};

const submitDiscussion = async (document: DocumentData, customFields: CustomField[]) => {
  submitDiscussionStatus.value = LoadingStatus.LOADING;
  const res = (await createNewDocument({
    document,
    continueToNextScreen: false,
    success: () => {
      void 0;
    },
    failed: (err: Error) => {
      console.error(err);
      submitDiscussionStatus.value = LoadingStatus.FAILED;
    },
  })) as CreateDocumentResult | null;

  if (res) {
    if (customFields.filter(field => field.name && field.value).length) {
      try {
        await updateTeam({
          teamID: res.team.team,
          updatedData: { custom_fields: customFields },
        });
      } catch (err) {
        console.error(err);
        submitDiscussionStatus.value = LoadingStatus.FAILED;
      }
    }
    submitDiscussionStatus.value = LoadingStatus.SUCCESS;
    closeModal();
    if (document.discussion_type === 'curate') {
      instance?.proxy?.$router.push({
        path: `/curatordebate/${res.document.document}`,
      });
    } else {
      instance?.proxy?.$router.push({
        path: `/debate/${res.document.document}`,
      });
    }
  } else submitDiscussionStatus.value = LoadingStatus.FAILED;
};

const closeModal = () => {
  if (discussionFormModal.value) {
    UIkit.modal(discussionFormModal.value).hide();
  }
};

onBeforeUnmount(() => {
  closeModal();
});
</script>

<template>
  <div>
    <BreadCrumb
      :path="[
        {
          title: `${userData.display_name}`,
          route: '',
        },
        {
          title: title,
          route: '/discussions',
        },
      ]"
    />
    <MainContentPanel
      :title="title"
      buttonIcon="Create-Discussions"
      buttonText="Add New"
      :tooltip="addNewDisable ? 'For future release' : 'Click here to begin a new discussion.'"
      :buttonAction="openBeginDiscussion"
      padding="small"
      :btnDisable="addNewDisable"
      buttonDataTid="add-new-discussion"
    >
      <UserDocuments :loadDataFunction="loadUserDocuments"></UserDocuments>
    </MainContentPanel>

    <div uk-modal ref="discussionFormModal" id="discussionFormModal">
      <BasicModal
        class="uk-padding-remove uk-width-2-3@s uk-width-1-3@xl moveableBasis"
        :showClose="false"
        style="width: 847px"
        id="moveableBasisForm"
        :resizeModal="true"
      >
        <NewBasisForm
          :submitDiscussionButtonStatus="submitDiscussionStatus"
          @submit="submitDiscussion"
          @close="closeModal"
        />
      </BasicModal>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.moveableBasis {
  position: absolute !important;
  left: 32%;
  z-index: 9;
  padding: 0;
}
</style>
